import { Box, Link, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { contentMaxWidth } from "src/design/tokens";

export const STANDARDS_URL = "https://www.whitehouse.gov/fitness/";

/** Year the seeded benchmark table (v1) took effect. */
export const BENCHMARKS_EFFECTIVE_YEAR = 2026;

export interface FooterProps {
  /** Version of the benchmark table in use; defaults to 1. */
  benchmarkVersion?: number;
  /** Year the benchmark table took effect; defaults to the v1 year. */
  effectiveYear?: number;
}

/** Site footer: test name, byline, not-affiliated notice, standards link and benchmark version. */
export const Footer = ({
  benchmarkVersion = 1,
  effectiveYear = BENCHMARKS_EFFECTIVE_YEAR,
}: FooterProps) => {
  const { t } = useTranslation();

  return (
    <Box
      component="footer"
      sx={(theme) => ({
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.tokens.surface,
      })}
    >
      <Box
        sx={{
          maxWidth: contentMaxWidth,
          mx: "auto",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "8px 16px",
          padding: "18px 32px",
          "@media (max-width: 767.95px)": { px: 2 },
        }}
      >
        <Typography
          component="span"
          sx={{
            maxWidth: 820,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.5,
            color: "text.secondary",
          }}
        >
          {t("app.testName")} · {t("app.byline")} · {t("app.notAffiliated")}{" "}
          <Link
            href={STANDARDS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-print-hide
            sx={(theme) => ({
              color: theme.palette.primaryInk,
              fontWeight: 600,
            })}
          >
            {t("app.seeStandards")}
          </Link>
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.5,
            color: "text.secondary",
            whiteSpace: "nowrap",
          }}
        >
          {t("console.benchmarksEffective", {
            version: benchmarkVersion,
            year: effectiveYear,
            defaultValue: "Benchmarks v{{version}} · Effective {{year}}",
          })}
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;
